import { Action, createReducer, on } from '@ngrx/store';
import { StartOverStateCleared } from '@store/actions/start-over/start-over.actions';

import {
  acceptMultiAccordion,
  resetMultiAccordionState,
  setExpandedMultiAccordion,
  unacceptMultiAccordion,
} from '../../actions/multi-terms-accordion/multi-terms-accordion.actions';

export enum MultiAccordionId {
  DOCUMENTS_AND_ELECTRONIC_CONSENT = 'documentsAndElectronicConsent',
  BACKUP_WITHHOLDING_IRA = 'backupWithholdingIra',
  BACKUP_WITHHOLDING_INDIVIDUAL_JOINT = 'backupWithholdingIndividualJoint',
  BACKUP_WITHHOLDING_TRUST_W8 = 'backupWithholdingTrustW8',
  SPOUSE_CERTIFICATION = 'spouseCertification',
  BENEFICIARY_CERTIFICATION = 'beneficiaryCertification',
  TRUSTEE_CERTIFICATION = 'trusteeCertification',
  SIGN_APPLICATION = 'signApplication',
}

export const TRUST_GROUP_ACCORDION_PREFIX = 'backupWithholdingTrustGroup_';
export const ESTATE_GROUP_ACCORDION_PREFIX = 'backupWithholdingEstateGroup_';
export const trustGroupAccordionId = (i: number): string => `${TRUST_GROUP_ACCORDION_PREFIX}${i}`;
export const estateGroupAccordionId = (i: number): string => `${ESTATE_GROUP_ACCORDION_PREFIX}${i}`;

export const buildAccordionOrder = (
  trustGroupCount: number,
  estateGroupCount: number,
): string[] => [
  MultiAccordionId.DOCUMENTS_AND_ELECTRONIC_CONSENT,
  MultiAccordionId.SPOUSE_CERTIFICATION,
  MultiAccordionId.BENEFICIARY_CERTIFICATION,
  MultiAccordionId.TRUSTEE_CERTIFICATION,
  MultiAccordionId.BACKUP_WITHHOLDING_IRA,
  MultiAccordionId.BACKUP_WITHHOLDING_INDIVIDUAL_JOINT,
  ...Array.from({ length: trustGroupCount }, (_, i) => trustGroupAccordionId(i)),
  MultiAccordionId.BACKUP_WITHHOLDING_TRUST_W8,
  ...Array.from({ length: estateGroupCount }, (_, i) => estateGroupAccordionId(i)),
  MultiAccordionId.SIGN_APPLICATION,
];

export interface MultiAccordionState {
  accepted: boolean;
  acceptedAt: string | null;
}

export type MultiAccordionsMap = {
  [key in MultiAccordionId]: MultiAccordionState;
} & { [key: string]: MultiAccordionState | undefined };

export interface MultiTermsAccordionState {
  accordions: MultiAccordionsMap;
  expandedAccordionId: MultiAccordionId | null;
}

export const initialState: MultiTermsAccordionState = {
  accordions: {
    [MultiAccordionId.DOCUMENTS_AND_ELECTRONIC_CONSENT]: { accepted: false, acceptedAt: null },
    [MultiAccordionId.BACKUP_WITHHOLDING_IRA]: { accepted: false, acceptedAt: null },
    [MultiAccordionId.BACKUP_WITHHOLDING_INDIVIDUAL_JOINT]: { accepted: false, acceptedAt: null },
    [MultiAccordionId.BACKUP_WITHHOLDING_TRUST_W8]: { accepted: false, acceptedAt: null },
    [MultiAccordionId.SPOUSE_CERTIFICATION]: { accepted: false, acceptedAt: null },
    [MultiAccordionId.BENEFICIARY_CERTIFICATION]: { accepted: false, acceptedAt: null },
    [MultiAccordionId.TRUSTEE_CERTIFICATION]: { accepted: false, acceptedAt: null },
    [MultiAccordionId.SIGN_APPLICATION]: { accepted: false, acceptedAt: null },
  },
  expandedAccordionId: MultiAccordionId.DOCUMENTS_AND_ELECTRONIC_CONSENT,
};

const multiTermsAccordionReducer = createReducer(
  initialState,
  on(acceptMultiAccordion, (state, { accordionId, acceptedAt }) => ({
    ...state,
    accordions: {
      ...state.accordions,
      [accordionId]: { accepted: true, acceptedAt },
    },
  })),
  on(unacceptMultiAccordion, (state, { accordionId }) => ({
    ...state,
    accordions: {
      ...state.accordions,
      [accordionId]: { accepted: false, acceptedAt: null },
    },
  })),
  on(setExpandedMultiAccordion, (state, { accordionId }) => ({
    ...state,
    expandedAccordionId: accordionId,
  })),
  on(resetMultiAccordionState, () => ({
    ...initialState,
  })),
  on(StartOverStateCleared, () => ({ ...initialState })),
);

export function reducer(state: MultiTermsAccordionState | undefined, action: Action) {
  return multiTermsAccordionReducer(state, action);
}

export const getMultiAccordions = (state: MultiTermsAccordionState) =>
  state?.accordions ?? initialState.accordions;

export const getAreAllMultiAccordionsAccepted = (state: MultiTermsAccordionState) => {
  if (!state?.accordions) {
    return false;
  }

  return Object.values(state.accordions).every((accordion) => accordion?.accepted);
};

export const getFirstUnacceptedMultiAccordionId = (
  state: MultiTermsAccordionState,
  order: string[],
): string | null => {
  if (!state?.accordions) {
    return MultiAccordionId.DOCUMENTS_AND_ELECTRONIC_CONSENT;
  }

  for (const accordionId of order) {
    if (!state.accordions[accordionId]?.accepted) {
      return accordionId;
    }
  }

  return null;
};

export const canMultiAccordionBeOpened = (
  state: MultiTermsAccordionState,
  accordionId: string,
  order: string[],
): boolean => {
  if (!state?.accordions) {
    return accordionId === MultiAccordionId.DOCUMENTS_AND_ELECTRONIC_CONSENT;
  }

  const accordionIndex = order.indexOf(accordionId);

  for (let i = 0; i < accordionIndex; i++) {
    if (!state.accordions[order[i]]?.accepted) {
      return false;
    }
  }

  return true;
};
